/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import FormControlLabel from '@mui/material/FormControlLabel';
import MuiRadio, { RadioProps as MuiRadioProps } from '@mui/material/Radio';
import MuiRadioGroup, { RadioGroupProps as MuiRadioGroupProps } from '@mui/material/RadioGroup';
import React from 'react';

import { Spacing } from 'src/common/ui/base/Spacing';

/**
 * Radio input component with optional label
 */
export const RadioInput = ({
  checked,
  className,
  'data-testid': dataTestid,
  disabled,
  id,
  label,
  name,
  onChange,
  required = false,
  value,
}: {
  'data-testid'?: string;
  label?: React.ReactNode;
} & Pick<
  MuiRadioProps,
  'checked' | 'className' | 'disabled' | 'id' | 'name' | 'onChange' | 'required' | 'value'
>) => {
  const radio = (
    <MuiRadio
      checked={checked}
      className={className}
      data-testid={dataTestid || 'nds-component-radio'}
      disabled={disabled}
      id={id}
      name={name}
      onChange={onChange}
      required={required}
      size="small"
      value={value}
      css={css`
        padding: ${Spacing.Unit1}px;
      `}
    />
  );

  if (label) {
    return (
      <FormControlLabel
        control={radio}
        label={label}
        disabled={disabled}
        css={css`
          margin: 0;

          .MuiFormControlLabel-label {
            position: relative;
            top: 1px; // Visually center the label with the checkbox
          }
        `}
      />
    );
  }

  return radio;
};

/**
 * RadioGroup component for grouping related radio inputs.
 * The RadioGroup component automatically manages the checked state of its children.
 *
 * @example
 * <RadioGroup
 *   name="options"
 *   value={selectedValue}
 *   onChange={(e) => setSelectedValue(e.target.value)}
 * >
 *   <RadioInput label="Option 1" value="option1" />
 *   <RadioInput label="Option 2" value="option2" />
 * </RadioGroup>
 */
export const RadioGroup = ({
  children,
  className,
  defaultValue,
  'data-testid': dataTestid,
  name,
  onChange,
  row = false,
  value,
}: {
  'data-testid'?: string;
  row?: boolean;
} & Pick<
  MuiRadioGroupProps,
  'children' | 'className' | 'defaultValue' | 'name' | 'onChange' | 'value'
>) => (
  <MuiRadioGroup
    className={className}
    data-testid={dataTestid || 'nds-component-radio-group'}
    defaultValue={defaultValue}
    name={name}
    onChange={onChange}
    row={row}
    value={value}
    css={css`
      display: flex;
      flex-direction: ${row ? 'row' : 'column'};
      gap: ${row ? Spacing.Unit4 : Spacing.Unit2}px;
    `}
  >
    {children}
  </MuiRadioGroup>
);
